import React, { useState, useEffect } from 'react';
import { formatDateTime } from '../../utils/dateUtils';
import { copyBackupCodes, downloadBackupCodes, printBackupCodes } from '../../utils/backupCodes';
import { UserProfileService, IPasswordUpdate } from '../../services/user-profile.service';
import { IUserProfile, IUserProfileUpdate } from '../../interfaces/user-profile.interface';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Typography,
    Box,
    Alert,
    IconButton,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { ImpersonationGrantService } from '../../services/impersonation-grant.service';
import { TwoFactorService, ITwoFactorStatus, ITwoFactorSetup } from '../../services/two-factor.service';
import { displayUsername } from '../../utils/userUtils';
import { IImpersonationGrant } from '../../interfaces/impersonation-grant.interface';
import { validatePassword } from '../../utils/passwordPolicy';

const DURATION_OPTIONS = [
    { label: '1 hour', value: 1 },
    { label: '4 hours', value: 4 },
    { label: '1 day', value: 24 },
    { label: '7 days', value: 168 },
];

// Profile-local enrollment confirm form. Distinct from `auth/TwoFactorEnroll`, which drives the
// LOGIN-time enrollment flow and completes via `useAuth().completeTwoFactorLogin`. This form is for
// an already-authenticated user enabling 2FA from their profile, so it completes via
// `TwoFactorService.enable` instead — never wire this to `completeTwoFactorLogin`.
const EnrollConfirmForm: React.FC<{ challengeId: string; onEnrolled: (codes: string[]) => void }> = ({ challengeId, onEnrolled }) => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const codes = await TwoFactorService.enable(challengeId, code);
            onEnrolled(codes);
        } catch (err: any) {
            setError(err?.response?.data || err?.message || 'Invalid or expired code');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
                label="Authentication Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                required
                size="small"
                sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" fullWidth disabled={submitting}>
                {submitting ? 'Confirming...' : 'Confirm'}
            </Button>
        </form>
    );
};

const UserProfile = () => {
    const [profile, setProfile] = useState<IUserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    const { user } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [formData, setFormData] = useState<IUserProfileUpdate>({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        bankName: '',
        bankAccountNumber: '',
        mobileBankingProvider: '',
        mobileBankingAccount: '',
    });
    const [passwordData, setPasswordData] = useState<IPasswordUpdate>({
        previousPassword: '',
        newPassword: '',
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState({ previousPassword: false, newPassword: false, confirmPassword: false });
    const navigate = useNavigate();

    // Grant access state
    const [activeGrant, setActiveGrant] = useState<IImpersonationGrant | null>(null);
    const [grantDialogOpen, setGrantDialogOpen] = useState(false);
    const [selectedDuration, setSelectedDuration] = useState<number>(1);
    const [grantLoading, setGrantLoading] = useState(false);

    // Two-factor authentication (self-service) state
    const [twoFactorStatus, setTwoFactorStatus] = useState<ITwoFactorStatus | null>(null);
    const [twoFactorSetup, setTwoFactorSetup] = useState<ITwoFactorSetup | null>(null);
    const [enrolledBackupCodes, setEnrolledBackupCodes] = useState<string[] | null>(null);
    const [savedConfirmed, setSavedConfirmed] = useState(false);
    const [disableCode, setDisableCode] = useState('');
    const [isDisabling2fa, setIsDisabling2fa] = useState(false);

    const isSystemAdmin = user?.roles === 'SystemAdmin';

    const newPasswordErrors = validatePassword(passwordData.newPassword, profile?.username, profile?.phone);

    useEffect(() => {
        fetchUserProfile();
        if (!isSystemAdmin) {
            fetchActiveGrant();
        }
        TwoFactorService.getStatus().then(setTwoFactorStatus).catch(() => setTwoFactorStatus(null));
    }, []);

    const fetchUserProfile = async () => {
        setLoading(true);
        try {
            const userProfile = await UserProfileService.getLoggedInUserProfile();
            setProfile(userProfile);
            setFormData({
                email: userProfile.email || '',
                firstName: userProfile.firstName || '',
                lastName: userProfile.lastName || '',
                phone: userProfile.phone || '',
                bankName: userProfile.bankName || '',
                bankAccountNumber: userProfile.bankAccountNumber || '',
                mobileBankingProvider: userProfile.mobileBankingProvider || '',
                mobileBankingAccount: userProfile.mobileBankingAccount || '',
            });
        } catch (err) {
            console.error('Failed to fetch profile:', err);
            toast.showApiError(err, 'Failed to load user profile. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveGrant = async () => {
        try {
            const grant = await ImpersonationGrantService.getMyGrant();
            setActiveGrant(grant);
        } catch {
            setActiveGrant(null);
        }
    };

    const handleGrantAccess = async () => {
        setGrantLoading(true);
        try {
            const grant = await ImpersonationGrantService.createGrant(selectedDuration);
            setActiveGrant(grant);
            setGrantDialogOpen(false);
            toast.showSuccess('Admin access granted successfully');
        } catch (err) {
            toast.showApiError(err, 'Failed to grant access');
        } finally {
            setGrantLoading(false);
        }
    };

    const handleRevokeGrant = async () => {
        setGrantLoading(true);
        try {
            await ImpersonationGrantService.revokeMyGrant();
            setActiveGrant(null);
            toast.showSuccess('Admin access revoked');
        } catch (err) {
            toast.showApiError(err, 'Failed to revoke access');
        } finally {
            setGrantLoading(false);
        }
    };

    const handleStartTwoFactorSetup = async () => {
        try {
            const setup = await TwoFactorService.setup();
            setTwoFactorSetup(setup);
        } catch (err) {
            toast.showApiError(err, 'Failed to start two-factor setup');
        }
    };

    const handleTwoFactorEnrolled = (backupCodes: string[]) => {
        setEnrolledBackupCodes(backupCodes);
    };

    const handleCloseEnrollDialog = async () => {
        setTwoFactorSetup(null);
        setEnrolledBackupCodes(null);
        setSavedConfirmed(false);
        const status = await TwoFactorService.getStatus();
        setTwoFactorStatus(status);
        toast.showSuccess('Two-factor authentication enabled');
    };

    const handleDisableTwoFactor = async () => {
        try {
            await TwoFactorService.disable(disableCode);
            setIsDisabling2fa(false);
            setDisableCode('');
            const status = await TwoFactorService.getStatus();
            setTwoFactorStatus(status);
            toast.showSuccess('Two-factor authentication disabled');
        } catch (err) {
            toast.showApiError(err, 'Failed to disable two-factor authentication');
        }
    };

    const handleRegenerateBackupCodes = async () => {
        const code = window.prompt('Enter your current authentication code to regenerate backup codes:');
        if (!code) return;
        try {
            await TwoFactorService.regenerateBackupCodes(code);
            const status = await TwoFactorService.getStatus();
            setTwoFactorStatus(status);
            toast.showSuccess('Backup codes regenerated. Check your account for the new codes.');
        } catch (err) {
            toast.showApiError(err, 'Failed to regenerate backup codes');
        }
    };

    // Profile-local enroll dialog (distinct from auth/TwoFactorEnroll, which drives the login-time flow).
    // Two-step: confirm the code via TwoFactorService.enable, then show the returned backup codes
    // before closing — mirrors the two-step UX in TwoFactorEnroll.tsx (Task 2).
    const renderEnrollDialog = () => {
        if (!twoFactorSetup) return null;
        return (
            <Dialog open onClose={() => { setTwoFactorSetup(null); setEnrolledBackupCodes(null); setSavedConfirmed(false); }}>
                <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
                <DialogContent sx={{ minWidth: 320, pt: 2 }}>
                    {enrolledBackupCodes ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Each code can be used once if you lose access to your authenticator app. Store them somewhere safe — they won't be shown again.
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2, fontFamily: 'monospace' }}>
                                {enrolledBackupCodes.map((c) => <div key={c}>{c}</div>)}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Button size="small" onClick={() => copyBackupCodes(enrolledBackupCodes).then(() => toast.showSuccess('Backup codes copied to clipboard'))}>Copy</Button>
                                <Button size="small" onClick={() => downloadBackupCodes(enrolledBackupCodes)}>Download</Button>
                                <Button size="small" onClick={() => printBackupCodes(enrolledBackupCodes)}>Print</Button>
                            </Box>
                            <FormControlLabel
                                control={<Checkbox checked={savedConfirmed} onChange={(e) => setSavedConfirmed(e.target.checked)} />}
                                label="I've saved these backup codes"
                                sx={{ mb: 2 }}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={!savedConfirmed}
                                onClick={handleCloseEnrollDialog}
                            >
                                Continue
                            </Button>
                        </>
                    ) : (
                        <>
                            <Box textAlign="center" sx={{ mb: 2 }}>
                                <img src={twoFactorSetup.qrDataUri} alt="Two-factor QR code" style={{ width: 200, height: 200 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Can't scan? Enter this code manually: <strong>{twoFactorSetup.secret}</strong>
                            </Typography>
                            <EnrollConfirmForm challengeId={twoFactorSetup.challengeId} onEnrolled={handleTwoFactorEnrolled} />
                        </>
                    )}
                </DialogContent>
            </Dialog>
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!profile) return;

        setLoading(true);
        try {
            const updatedProfile = await UserProfileService.updateUserProfile(
                profile.id,
                formData
            );
            setProfile(updatedProfile);
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to update profile:', err);
            toast.showApiError(err, 'Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'confirmPassword') {
            setConfirmPassword(value);
        } else {
            setPasswordData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (passwordData.newPassword !== confirmPassword) {
            toast.showError('New password and confirm password do not match');
            return;
        }

        if (newPasswordErrors.length > 0) {
            toast.showError('Password does not meet the strength requirements');
            return;
        }

        setLoading(true);

        try {
            await UserProfileService.updatePassword(passwordData);
            toast.showSuccess('Password updated successfully!');
            setIsChangingPassword(false);
            setPasswordData({ previousPassword: '', newPassword: '' });
            setConfirmPassword('');
        } catch (err: any) {
            toast.showApiError(err, 'Failed to update password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading && !profile) {
        return <div>Loading profile...</div>;
    }

    return (
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
            <h2>User Profile</h2>

            {!isEditing && !isChangingPassword ? (
                <div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Email:</strong> {profile?.email}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>First Name:</strong> {profile?.firstName}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Last Name:</strong> {profile?.lastName}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Phone:</strong> {profile?.phone}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Username:</strong> {displayUsername(profile?.username)}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Type:</strong> {profile?.type}
                    </div>
                    <div style={{ marginTop: '20px', marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 10px 0' }}>Banking</h3>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Bank Name:</strong> {profile?.bankName || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Bank Account Number:</strong> {profile?.bankAccountNumber || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Mobile Banking Provider:</strong> {profile?.mobileBankingProvider || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Mobile Banking Account:</strong> {profile?.mobileBankingAccount || '-'}
                    </div>
                    <button
                        onClick={() => setIsEditing(true)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        Edit Profile
                    </button>
                    <button
                        onClick={() => setIsChangingPassword(true)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#ffc107',
                            color: 'black',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        Change Password
                    </button>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Back
                    </button>

                    {/* Grant Access Section - all non-SystemAdmin users */}
                    {!isSystemAdmin && (
                        <Box sx={{ mt: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom>Admin Access Grant</Typography>
                            {activeGrant ? (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Active grant — expires {formatDateTime(activeGrant.expiresAt)}
                                    </Alert>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleRevokeGrant}
                                        disabled={grantLoading}
                                    >
                                        Revoke Access
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Grant SystemAdmin temporary access to manage your institution's data.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        onClick={() => setGrantDialogOpen(true)}
                                        disabled={grantLoading}
                                    >
                                        Grant Admin Access
                                    </Button>
                                </>
                            )}
                        </Box>
                    )}

                    <Box sx={{ mt: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>Two-Factor Authentication</Typography>
                        {twoFactorStatus?.enabled ? (
                            <>
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Enabled{twoFactorStatus.backupCodesDisabled ? ' — backup codes blocked by an administrator' : ''}
                                </Alert>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {twoFactorStatus.backupCodesRemaining} backup codes remaining.
                                </Typography>
                                {isDisabling2fa ? (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            label="Current code"
                                            size="small"
                                            value={disableCode}
                                            onChange={(e) => setDisableCode(e.target.value)}
                                        />
                                        <Button variant="contained" color="error" onClick={handleDisableTwoFactor}>
                                            Confirm Disable
                                        </Button>
                                        <Button variant="outlined" onClick={() => { setIsDisabling2fa(false); setDisableCode(''); }}>
                                            Cancel
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button variant="outlined" color="error" onClick={() => setIsDisabling2fa(true)}>
                                            Disable Two-Factor Authentication
                                        </Button>
                                        {!twoFactorStatus.backupCodesDisabled && (
                                            <Button variant="outlined" onClick={handleRegenerateBackupCodes}>
                                                Regenerate Backup Codes
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Add an extra layer of security to your account with an authenticator app.
                                </Typography>
                                <Button variant="contained" onClick={handleStartTwoFactorSetup}>
                                    Enable Two-Factor Authentication
                                </Button>
                            </>
                        )}
                    </Box>

                </div>
            ) : isChangingPassword ? (
                <form onSubmit={handlePasswordSubmit}>
                    <h3>Change Password</h3>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Current Password:
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.previousPassword ? 'text' : 'password'}
                                name="previousPassword"
                                value={passwordData.previousPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                            />
                            <IconButton
                                type="button"
                                onClick={() => setShowPasswords(s => ({ ...s, previousPassword: !s.previousPassword }))}
                                size="small"
                                aria-label={showPasswords.previousPassword ? 'hide password' : 'show password'}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.previousPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            New Password:
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.newPassword ? 'text' : 'password'}
                                name="newPassword"
                                value={passwordData.newPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                                minLength={3}
                            />
                            <IconButton
                                type="button"
                                onClick={() => setShowPasswords(s => ({ ...s, newPassword: !s.newPassword }))}
                                size="small"
                                aria-label={showPasswords.newPassword ? 'hide password' : 'show password'}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.newPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                        {passwordData.newPassword.length > 0 && newPasswordErrors.length > 0 && (
                            <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>
                                {newPasswordErrors.join('; ')}
                            </div>
                        )}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Confirm New Password:
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.confirmPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                value={confirmPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                                minLength={3}
                            />
                            <IconButton
                                type="button"
                                onClick={() => setShowPasswords(s => ({ ...s, confirmPassword: !s.confirmPassword }))}
                                size="small"
                                aria-label={showPasswords.confirmPassword ? 'hide password' : 'show password'}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.confirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading || newPasswordErrors.length > 0}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        {loading ? 'Updating...' : 'Update Password'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setIsChangingPassword(false);
                            setPasswordData({ previousPassword: '', newPassword: '' });
                            setConfirmPassword('');
                        }}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                </form>
            ) : (
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Email:
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            First Name:
                        </label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Last Name:
                        </label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Phone:
                        </label>
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginTop: '20px', marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 10px 0' }}>Banking</h3>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Bank Name:
                        </label>
                        <input
                            type="text"
                            name="bankName"
                            value={formData.bankName || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Bank Account Number:
                        </label>
                        <input
                            type="text"
                            name="bankAccountNumber"
                            value={formData.bankAccountNumber || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Mobile Banking Provider:
                        </label>
                        <input
                            type="text"
                            name="mobileBankingProvider"
                            value={formData.mobileBankingProvider || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Mobile Banking Account:
                        </label>
                        <input
                            type="text"
                            name="mobileBankingAccount"
                            value={formData.mobileBankingAccount || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Username:</strong> {displayUsername(profile?.username)}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Type:</strong> {profile?.type}
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                </form>
            )}

            {/* Grant Duration Dialog */}
            <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)}>
                <DialogTitle>Grant Admin Access</DialogTitle>
                <DialogContent sx={{ minWidth: 320, pt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select how long SystemAdmin should have access to your institution data.
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Duration</InputLabel>
                        <Select
                            value={selectedDuration}
                            label="Duration"
                            onChange={(e) => setSelectedDuration(Number(e.target.value))}
                        >
                            {DURATION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleGrantAccess}
                        disabled={grantLoading}
                    >
                        Grant Access
                    </Button>
                </DialogActions>
            </Dialog>

            {renderEnrollDialog()}
        </div>
    );
};

export default UserProfile;
